import { captureMessage } from '@tink/core/src/sentry';
import { OauthStoreDescription } from '../stores/oauth-store';
import { Analytics } from '../core/Analytics/tink-link-analytics';
import { ApplicationEvent, PageView } from '../models/analytics';

export interface ApplicationResult {
  buildQueryPath: () => string;
  buildIframeMessage: () => string;
}

export const followingEmbeddedRule = (oauthDescription?: OauthStoreDescription) => {
  const isAppEmbedded = window.top !== window.self;
  const isEmbeddedAppAllowed = Boolean(oauthDescription && oauthDescription.embeddedAllowed);

  if (isEmbeddedAppAllowed === false && isAppEmbedded) {
    return false;
  }

  return true;
};

type PostNotificationHandlerArguments = {
  isIframe: boolean;
  redirectUri: string;
  oauthState?: string;
  window: Window;
};

export class PostNotificationHandler {
  arguments: PostNotificationHandlerArguments = {
    isIframe: false,
    redirectUri: '',
    window,
  };

  isExecuting = false;

  oauthDescription?: OauthStoreDescription = undefined;

  initialize(args: PostNotificationHandlerArguments) {
    this.arguments = args;
  }

  private updateWindowTitle(applicationResult: ApplicationResult) {
    const { window } = this.arguments;

    window.document.title = applicationResult.buildQueryPath();
  }

  private postMessageToParent(applicationResult: ApplicationResult) {
    const { window, redirectUri } = this.arguments;

    window.parent.postMessage(applicationResult.buildIframeMessage(), redirectUri);
  }

  private async redirectToCustomer(
    applicationResult: ApplicationResult,
    analyticsHandler: Analytics | null = null
  ) {
    const { window } = this.arguments;
    const customerRedirectUrl = this.buildCustomerRedirectUrl(applicationResult);

    try {
      if (analyticsHandler) {
        await analyticsHandler.sendInteractionEventOnce({
          view: PageView.SUCCESS_SCREEN,
          label: customerRedirectUrl,
          action: ApplicationEvent.REDIRECT_BY_SETTING_LOCATION,
        });
      }

      window.location.href = customerRedirectUrl;
    } catch (error) {
      captureMessage('Redirecting user to client failed', {
        custom: {
          redirectUrl: customerRedirectUrl,
        },
      });
    }
  }

  buildCustomerRedirectUrl(applicationResult: ApplicationResult) {
    const { redirectUri, oauthState } = this.arguments;

    const [redirectUrl, redirectQueryParams] = decodeURIComponent(redirectUri).split('?');
    const responseQueryParams = applicationResult.buildQueryPath();
    const resultQueryParams = [redirectQueryParams, responseQueryParams].filter(Boolean).join('&');

    let location = `${redirectUrl}?${resultQueryParams}`;

    if (oauthState) {
      location = `${location}&state=${oauthState}`;
    }

    return location;
  }

  setOauthDescription(oauthDescription?: OauthStoreDescription) {
    this.oauthDescription = oauthDescription;
  }

  isAppAllowedToNotifyCustomerBack() {
    if (!this.oauthDescription) {
      return false;
    }

    return followingEmbeddedRule(this.oauthDescription);
  }

  execute(applicationResult: ApplicationResult, analyticsHandler: Analytics | null = null) {
    if (this.isExecuting) {
      return;
    }

    this.isExecuting = true;

    const { isIframe, redirectUri } = this.arguments;
    const isAppAllowedToNotifyCustomer = this.isAppAllowedToNotifyCustomerBack();

    if (!isAppAllowedToNotifyCustomer) {
      // eslint-disable-next-line no-console
      console.log(
        `App is not allowed to notify customer back. ${applicationResult.buildQueryPath()}`
      );

      return;
    }

    if (!redirectUri) {
      // eslint-disable-next-line no-console
      console.log(`Cannot notify customer: \`redirect_uri\` url parameter is missing`);

      return;
    }

    if (isIframe) {
      this.postMessageToParent(applicationResult);
    } else if (redirectUri === 'urn:ietf:wg:oauth:2.0:oob') {
      this.updateWindowTitle(applicationResult);
    } else {
      this.redirectToCustomer(applicationResult, analyticsHandler);
    }
  }
}

export const postNotificationHandler = new PostNotificationHandler();
